/**
 * MessageListItem
 *
 * A single row in the message list showing sender, subject, snippet,
 * date, star toggle, read status, and attachment indicator.
 */

import { Star, Paperclip } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

interface MessageListItemProps {
  id: string;
  fromName: string | null;
  fromAddress: string;
  subject: string;
  snippet: string | null;
  date: string | null;
  isRead: boolean;
  isStarred: boolean;
  hasAttachments: boolean;
  isSelected: boolean;
  isActive: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onClick: (id: string) => void;
  onDoubleClick?: (id: string) => void;
  onToggleStar: (id: string) => void;
}

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function senderInitial(name: string | null, address: string): string {
  const display = name || address;
  return display.charAt(0).toUpperCase();
}

export function MessageListItem({
  id,
  fromName,
  fromAddress,
  subject,
  snippet,
  date,
  isRead,
  isStarred,
  hasAttachments,
  isSelected,
  isActive,
  onSelect,
  onClick,
  onDoubleClick,
  onToggleStar,
}: MessageListItemProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 border-b border-border cursor-pointer transition-colors',
        'hover:bg-muted/50',
        isActive && 'bg-muted',
        !isRead && 'bg-primary/5',
      )}
      onClick={() => onClick(id)}
      onDoubleClick={() => onDoubleClick?.(id)}
      role="row"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick(id);
      }}
    >
      {/* Checkbox */}
      <div
        className="flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelect(id, !!checked)}
          aria-label="Select message"
        />
      </div>

      {/* Star */}
      <button
        className="flex-shrink-0 p-0.5 rounded hover:bg-muted"
        onClick={(e) => {
          e.stopPropagation();
          onToggleStar(id);
        }}
        aria-label={isStarred ? 'Unstar' : 'Star'}
      >
        <Star
          className={cn(
            'h-4 w-4',
            isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground',
          )}
        />
      </button>

      {/* Avatar initial */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
        {senderInitial(fromName, fromAddress)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-sm truncate',
              !isRead ? 'font-semibold text-foreground' : 'text-muted-foreground',
            )}
          >
            {fromName || fromAddress}
          </span>
          <span className="text-xs text-muted-foreground flex-shrink-0">
            {formatRelativeDate(date)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span
            className={cn(
              'text-sm truncate',
              !isRead ? 'font-medium text-foreground' : 'text-muted-foreground',
            )}
          >
            {subject || '(no subject)'}
          </span>
          {hasAttachments && <Paperclip className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
        </div>
        <p className="text-xs text-muted-foreground truncate">{snippet || ''}</p>
      </div>
    </div>
  );
}
